/**
 * Time Since Records Table Component
 * Displays and manages time since records in a table format
 */

import React, { useState } from 'react';
import {
  Grid,
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  FormControl,
  Select,
  MenuItem,
  IconButton,
  Divider,
  Tooltip,
  Popover,
  Alert,
  Autocomplete
} from '@mui/material';
import { Add, Delete, InfoOutlined, Calculate } from '@mui/icons-material';
import { HHMM_LIMIT_HELPER_TEXT } from '../../constants/hhmmHoursLimits';
import { formatHoursValue, validateHoursInput, validateCyclesInput } from './assemblyFormUtils';
import { elapsedIsCalendarBased, TSR_TIME_UNITS, isDecimalNumericTsrUnit } from '../components/componentFormUtils';
import HoursConverterCalculatorDialog from './HoursConverterCalculatorDialog';

const TimeSinceRecordsTable = ({
  timeSinceRecords,
  handleAddTimeSinceRecord,
  handleUpdateTimeSinceRecord,
  handleRemoveTimeSinceRecord,
  validateYearsInput,
  validateCalendarDaysInput,
  validateCyclesInput: validateCyclesInputProp,
  isAirframeAssembly = false,
  readOnly = false,
  labelOptions = []
}) => {
  const validateCycles = validateCyclesInputProp || validateCyclesInput;
  const [validationErrors, setValidationErrors] = useState({});
  const [typeInfoAnchor, setTypeInfoAnchor] = useState(null);
  const [limitInfoAnchor, setLimitInfoAnchor] = useState(null);
  const [hoursCalculatorOpen, setHoursCalculatorOpen] = useState(false);

  const hasAnyHours = timeSinceRecords.some(
    (r) =>
      r.limit?.unit === 'Hours' ||
      r.elapsedTime?.unit === 'Hours' ||
      r.remainingTime?.unit === 'Hours'
  );

  const setFieldError = (recordIndex, field, error) => {
    setValidationErrors(prev => ({
      ...prev,
      [`${recordIndex}-${field}`]: error
    }));
  };

  const getFieldError = (recordIndex, field) => {
    return validationErrors[`${recordIndex}-${field}`] || '';
  };

  const unitForRecord = (record) => record.limit?.unit || record.elapsedTime?.unit || 'Hours';

  const renderValueAtInstallationCell = (record, idx, fieldKey, errorKey, disabled = false) => {
    const unit = unitForRecord(record);
    const val = record[fieldKey]?.value != null ? record[fieldKey].value : '';
    const displayVal = (unit === 'Calendar Days' || unit === 'Landings') && val && val.includes('.')
      ? Math.floor(parseFloat(val) || 0).toString()
      : val;
    return (
      <Grid container spacing={1} key={fieldKey}>
        <Grid item xs={7}>
          <TextField
            size="small"
            fullWidth
            type="text"
            value={displayVal}
            disabled={disabled}
            onChange={(e) => {
              let validatedValue = e.target.value;
              if (unit === 'Hours') {
                const validation = validateHoursInput(e.target.value);
                validatedValue = validation.value;
                setFieldError(idx, errorKey, validation.error);
              } else if (unit === 'Years') {
                validatedValue = validateYearsInput(e.target.value);
                setFieldError(idx, errorKey, '');
              } else if (unit === 'Calendar Days' || unit === 'Landings') {
                validatedValue = validateCalendarDaysInput(e.target.value);
                setFieldError(idx, errorKey, '');
              } else if (isDecimalNumericTsrUnit(unit)) {
                validatedValue = validateCycles(e.target.value);
                setFieldError(idx, errorKey, '');
              } else setFieldError(idx, errorKey, '');
              handleUpdateTimeSinceRecord(idx, `${fieldKey}.value`, validatedValue);
            }}
            onBlur={(e) => {
              if (unit === 'Hours' && e.target.value) {
                const formattedValue = formatHoursValue(e.target.value);
                if (formattedValue !== e.target.value) {
                  handleUpdateTimeSinceRecord(idx, `${fieldKey}.value`, formattedValue);
                  setFieldError(idx, errorKey, '');
                }
              }
            }}
            placeholder={disabled ? 'N/A' : (unit === 'Hours' ? 'HH:MM' : unit === 'Years' ? 'YY.MM' : '0')}
            helperText={!disabled && unit === 'Hours' ? (getFieldError(idx, errorKey) || HHMM_LIMIT_HELPER_TEXT) : undefined}
            error={!disabled && unit === 'Hours' && !!getFieldError(idx, errorKey)}
            sx={{ '& .MuiInputBase-input': { fontSize: '0.75rem' }, ...(disabled && { '& .MuiInputBase-input': { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } }) }}
          />
        </Grid>
        <Grid item xs={5}>
          <Typography variant="body2" sx={{ fontSize: '0.75rem', py: 1, color: 'text.secondary' }}>
            {unit}
          </Typography>
        </Grid>
      </Grid>
    );
  };

  return (
    <>
      <Grid item xs={12}>
        <Divider sx={{ my: 2 }} />
      </Grid>
      <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6">Time Since Records</Typography>
            {hasAnyHours && (
              <Tooltip title="Hours converter (decimal ⇄ HH:MM)">
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => setHoursCalculatorOpen(true)}
                  aria-label="Open hours converter calculator"
                >
                  <Calculate fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          {!readOnly && (
            <Button
              size="small"
              startIcon={<Add />}
              onClick={handleAddTimeSinceRecord}
            >
              Add Record
            </Button>
          )}
        </Box>
        <HoursConverterCalculatorDialog
          open={hoursCalculatorOpen}
          onClose={() => setHoursCalculatorOpen(false)}
        />
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '100px' }}>
                  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                    Type
                    <Tooltip title="Info">
                      <IconButton
                        size="small"
                        color="info"
                        onClick={(e) => setTypeInfoAnchor(e.currentTarget)}
                        sx={{ p: 0.25 }}
                        aria-label="Time since record types info"
                      >
                        <InfoOutlined sx={{ fontSize: '1rem' }} />
                      </IconButton>
                    </Tooltip>
                    <Popover
                      open={Boolean(typeInfoAnchor)}
                      anchorEl={typeInfoAnchor}
                      onClose={() => setTypeInfoAnchor(null)}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    >
                      <Alert severity="info" sx={{ m: 1.5, maxWidth: 360 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                          Time Since Record Types:
                        </Typography>
                        <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.5 } }}>
                          <li><strong>TSN</strong>: Time Since New</li>
                          <li><strong>TSO</strong>: Time Since Overhaul</li>
                          <li><strong>TSI</strong>: Time Since Inspection</li>
                          <li><strong>TSP</strong>: Time Since Part</li>
                          <li><strong>TSR</strong>: Time Since Repair</li>
                        </Box>
                      </Alert>
                    </Popover>
                  </Box>
                </TableCell>
                <TableCell sx={{ minWidth: '220px', width: '30%' }}>Label</TableCell>
                <TableCell sx={{ width: '200px' }}>
                  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                    Life Limit (or) Frequency of THIS Assembly itself
                    <Tooltip title="Limit actions by type">
                      <IconButton size="small" color="info" onClick={(e) => setLimitInfoAnchor(e.currentTarget)} sx={{ p: 0.25 }} aria-label="Limit info">
                        <InfoOutlined sx={{ fontSize: '1rem' }} />
                      </IconButton>
                    </Tooltip>
                    <Popover open={Boolean(limitInfoAnchor)} anchorEl={limitInfoAnchor} onClose={() => setLimitInfoAnchor(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} transformOrigin={{ vertical: 'top', horizontal: 'left' }}>
                      <Alert severity="info" sx={{ m: 1.5, maxWidth: 380 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>When limit is reached:</Typography>
                        <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.5 } }}>
                          <li><strong>TSN</strong>: Discard / Repair / Overhaul</li>
                          <li><strong>TSO</strong>: Overhaul</li>
                          <li><strong>TSI</strong>: Re-inspect</li>
                          <li><strong>TSP</strong>/<strong>TSR</strong>: As per maintenance program</li>
                        </Box>
                      </Alert>
                    </Popover>
                  </Box>
                </TableCell>
                <TableCell sx={{ width: '200px' }}>
                  <Box>
                    <Box component="span">Elapsed value of THIS Assembly itself at Installation</Box>
                    <Typography variant="caption" sx={{ display: 'block', fontSize: '0.65rem', color: 'text.secondary' }}>
                      Snapshot of how much this assembly has used at the moment of installation. Live elapsed will continue from this value.
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell sx={{ width: '160px' }}>Airframe Value at Installation</TableCell>
                <TableCell sx={{ width: '160px' }}>Parent Assembly&apos;s Value at Installation</TableCell>
                <TableCell sx={{ width: '180px' }}>Remaining</TableCell>
                <TableCell sx={{ width: '100px' }}>Start Date</TableCell>
                <TableCell sx={{ width: '100px' }}>As On Date</TableCell>
                <TableCell sx={{ width: '120px' }}>Due At Parent Assembly</TableCell>
                <TableCell sx={{ width: '120px' }}>Due At Airframe Assembly</TableCell>
                <TableCell sx={{ width: '120px' }}>Due At This Itself</TableCell>
                <TableCell sx={{ width: '80px' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {timeSinceRecords.map((record, index) => {
                const normalizedOptions = Array.isArray(labelOptions)
                  ? labelOptions
                      .map(opt => (opt || '').trim().toLowerCase())
                      .filter(Boolean)
                  : [];
                const currentLabel = (record.label || '').trim();
                const isUnmappedLabel =
                  !!currentLabel &&
                  normalizedOptions.length > 0 &&
                  !normalizedOptions.includes(currentLabel.toLowerCase());
                const disableInstallSnapshots = readOnly || elapsedIsCalendarBased(record.elapsedTime?.unit);

                return (
                <TableRow key={index}>
                  <TableCell sx={{ width: '100px' }}>
                    <FormControl size="small" fullWidth>
                      <Select
                        value={record.type}
                        onChange={(e) => handleUpdateTimeSinceRecord(index, 'type', e.target.value)}
                        disabled={readOnly}
                        sx={{ fontSize: '0.75rem' }}
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              maxHeight: 400,
                              minWidth: 300,
                              '& .MuiMenuItem-root': {
                                whiteSpace: 'normal',
                                wordBreak: 'break-word',
                                padding: '8px 12px',
                                minHeight: 'auto',
                                lineHeight: 1.4,
                                overflow: 'visible',
                                color: '#000',
                                fontSize: '0.75rem',
                                '&:hover': {
                                  backgroundColor: '#f5f5f5'
                                }
                              }
                            }
                          }
                        }}
                      >
                        <MenuItem value="TSN" sx={{ fontSize: '0.75rem' }}>TSN</MenuItem>
                        <MenuItem value="TSO" sx={{ fontSize: '0.75rem' }}>TSO</MenuItem>
                        <MenuItem value="TSI" sx={{ fontSize: '0.75rem' }}>TSI</MenuItem>
                        <MenuItem value="TSP" sx={{ fontSize: '0.75rem' }}>TSP</MenuItem>
                        <MenuItem value="TSR" sx={{ fontSize: '0.75rem' }}>TSR</MenuItem>
                        <MenuItem value="SLL" sx={{ fontSize: '0.75rem' }}>SLL</MenuItem>
                        <MenuItem value="OTL" sx={{ fontSize: '0.75rem' }}>OTL</MenuItem>
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell sx={{ minWidth: '220px', width: '30%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ flex: 1 }}>
                        {Array.isArray(labelOptions) && labelOptions.length > 0 ? (
                          <Autocomplete
                            size="small"
                            freeSolo
                            disableClearable={false}
                            options={labelOptions}
                            value={record.label || ''}
                            readOnly={readOnly}
                            onChange={(_, newValue) => {
                              if (!readOnly) {
                                handleUpdateTimeSinceRecord(index, 'label', newValue || '');
                              }
                            }}
                            onInputChange={(_, newInputValue, reason) => {
                              if (!readOnly && reason === 'input') {
                                handleUpdateTimeSinceRecord(index, 'label', newInputValue || '');
                              }
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                fullWidth
                                InputProps={{
                                  ...params.InputProps,
                                  readOnly
                                }}
                                sx={{ '& .MuiInputBase-input': { fontSize: '0.75rem' } }}
                              />
                            )}
                          />
                        ) : (
                          <TextField
                            size="small"
                            fullWidth
                            value={record.label}
                            onChange={(e) => handleUpdateTimeSinceRecord(index, 'label', e.target.value)}
                            InputProps={{ readOnly }}
                            sx={{ '& .MuiInputBase-input': { fontSize: '0.75rem' } }}
                          />
                        )}
                      </Box>
                      {isUnmappedLabel && (
                        <Tooltip title="This label does not match the current master/custom configuration for this assembly type, but will be saved as-is.">
                          <InfoOutlined sx={{ fontSize: '1rem' }} color="warning" />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ width: '180px' }}>
                    <Grid container spacing={1}>
                      <Grid item xs={7}>
                        <TextField
                          size="small"
                          fullWidth
                          type="text"
                          value={
                            record.limit?.value 
                              ? (record.limit?.unit === 'Calendar Days' && record.limit.value.includes('.'))
                                ? Math.floor(parseFloat(record.limit.value) || 0).toString()
                                : (record.limit?.unit === 'Landings' && record.limit.value.includes('.'))
                                ? Math.floor(parseFloat(record.limit.value) || 0).toString()
                                : record.limit.value
                              : ''
                          }
                          onChange={(e) => {
                            let validatedValue = e.target.value;
                            let error = '';
                            
                            if (record.limit?.unit === 'Hours') {
                              const validation = validateHoursInput(e.target.value);
                              validatedValue = validation.value;
                              error = validation.error;
                              setFieldError(index, 'limit', error);
                            } else if (record.limit?.unit === 'Years') {
                              validatedValue = validateYearsInput(e.target.value);
                              setFieldError(index, 'limit', '');
                            } else if (record.limit?.unit === 'Calendar Days') {
                              validatedValue = validateCalendarDaysInput(e.target.value);
                              setFieldError(index, 'limit', '');
                            } else if (isDecimalNumericTsrUnit(record.limit?.unit)) {
                              validatedValue = validateCycles(e.target.value);
                              setFieldError(index, 'limit', '');
                            } else if (record.limit?.unit === 'Landings') {
                              validatedValue = validateCalendarDaysInput(e.target.value);
                              setFieldError(index, 'limit', '');
                            }
                            
                            if (!readOnly) {
                              handleUpdateTimeSinceRecord(index, 'limit.value', validatedValue);
                            }
                          }}
                          onBlur={(e) => {
                            // Format Hours value to ensure colon is present
                              if (!readOnly && record.limit?.unit === 'Hours' && e.target.value) {
                              const formattedValue = formatHoursValue(e.target.value);
                              if (formattedValue !== e.target.value) {
                                handleUpdateTimeSinceRecord(index, 'limit.value', formattedValue);
                                // Clear error after formatting
                                setFieldError(index, 'limit', '');
                              }
                            }
                          }}
                          placeholder={record.limit?.unit === 'Hours' ? 'HH:MM' : record.limit?.unit === 'Years' ? 'YY.MM (e.g., 2.10)' : '0'}
                          helperText={
                            record.limit?.unit === 'Hours'
                              ? getFieldError(index, 'limit') || HHMM_LIMIT_HELPER_TEXT
                              : record.limit?.unit === 'Years' 
                              ? 'Format: YY.MM (years.months, max 2 digits for years, 0-12 for months)'
                              : (record.limit?.unit === 'Calendar Days' || record.limit?.unit === 'Landings')
                              ? 'Numeric format (integer)'
                              : isDecimalNumericTsrUnit(record.limit?.unit)
                              ? 'Numeric format (up to 2 decimal places)'
                              : ''
                          }
                          error={record.limit?.unit === 'Hours' && !!getFieldError(index, 'limit')}
                          sx={{ '& .MuiInputBase-input': { fontSize: '0.75rem' } }}
                        />
                      </Grid>
                      <Grid item xs={5}>
                        <FormControl size="small" fullWidth>
                          <Select
                            value={record.limit?.unit || 'Hours'}
                            onChange={(e) => handleUpdateTimeSinceRecord(index, 'limit.unit', e.target.value)}
                            disabled={readOnly}
                            sx={{ fontSize: '0.75rem' }}
                            MenuProps={{
                              PaperProps: {
                                sx: {
                                  maxHeight: 400,
                                  minWidth: 200,
                                  '& .MuiMenuItem-root': {
                                    whiteSpace: 'normal',
                                    wordBreak: 'break-word',
                                    padding: '8px 12px',
                                    minHeight: 'auto',
                                    lineHeight: 1.4,
                                    overflow: 'visible',
                                    color: '#000',
                                    fontSize: '0.75rem',
                                    '&:hover': {
                                      backgroundColor: '#f5f5f5'
                                    }
                                  }
                                }
                              }
                            }}
                          >
                            {TSR_TIME_UNITS.map((unit) => (
                              <MenuItem key={unit} value={unit} sx={{ fontSize: '0.75rem' }}>{unit}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </TableCell>
                  <TableCell sx={{ width: '180px' }}>
                    <Grid container spacing={1}>
                      <Grid item xs={7}>
                        <TextField
                          size="small"
                          fullWidth
                          type="text"
                          value={
                            record.assemblySelfValueAtInstallation?.value != null
                              ? record.assemblySelfValueAtInstallation.value
                              : (record.elapsedTime?.value || '')
                          }
                          onChange={(e) => {
                            let validatedValue = e.target.value;
                            let error = '';
                            
                            if (record.elapsedTime?.unit === 'Hours') {
                              const validation = validateHoursInput(e.target.value);
                              validatedValue = validation.value;
                              error = validation.error;
                              setFieldError(index, 'elapsed', error);
                            } else if (record.elapsedTime?.unit === 'Years') {
                              validatedValue = validateYearsInput(e.target.value);
                              setFieldError(index, 'elapsed', '');
                            } else if (record.elapsedTime?.unit === 'Calendar Days') {
                              validatedValue = validateCalendarDaysInput(e.target.value);
                              setFieldError(index, 'elapsed', '');
                            } else if (isDecimalNumericTsrUnit(record.elapsedTime?.unit)) {
                              validatedValue = validateCycles(e.target.value);
                              setFieldError(index, 'elapsed', '');
                            } else if (record.elapsedTime?.unit === 'Landings') {
                              validatedValue = validateCalendarDaysInput(e.target.value);
                              setFieldError(index, 'elapsed', '');
                            }
                            
                            if (!readOnly) {
                              handleUpdateTimeSinceRecord(index, 'elapsedTime.value', validatedValue);
                            }
                          }}
                          onBlur={(e) => {
                            // Format Hours value to ensure colon is present
                            if (!readOnly && record.elapsedTime?.unit === 'Hours' && e.target.value) {
                              const formattedValue = formatHoursValue(e.target.value);
                              if (formattedValue !== e.target.value) {
                                handleUpdateTimeSinceRecord(index, 'elapsedTime.value', formattedValue);
                                // Clear error after formatting
                                setFieldError(index, 'elapsed', '');
                              }
                            }
                          }}
                          placeholder={record.elapsedTime?.unit === 'Hours' ? 'HH:MM' : record.elapsedTime?.unit === 'Years' ? 'YY.MM (e.g., 2.10)' : '0'}
                          helperText={
                            record.elapsedTime?.unit === 'Hours'
                              ? getFieldError(index, 'elapsed') || HHMM_LIMIT_HELPER_TEXT
                              : record.elapsedTime?.unit === 'Years' 
                              ? (record.startDate && record.asOnDate 
                                  ? 'Auto-calculated from dates (format: YY.MM, can be overridden)' 
                                  : 'Format: YY.MM (years.months, max 2 digits for years, 0-12 for months)')
                              : (record.elapsedTime?.unit === 'Calendar Days' || record.elapsedTime?.unit === 'Landings')
                              ? 'Numeric format (integer)'
                              : isDecimalNumericTsrUnit(record.elapsedTime?.unit)
                              ? 'Numeric format (up to 2 decimal places)'
                              : ''
                          }
                          error={record.elapsedTime?.unit === 'Hours' && !!getFieldError(index, 'elapsed')}
                          sx={{ '& .MuiInputBase-input': { fontSize: '0.75rem' } }}
                        />
                      </Grid>
                      <Grid item xs={5}>
                        <FormControl size="small" fullWidth>
                          <Select
                            value={record.elapsedTime?.unit || 'Hours'}
                            onChange={(e) => handleUpdateTimeSinceRecord(index, 'elapsedTime.unit', e.target.value)}
                            disabled={readOnly}
                            sx={{ fontSize: '0.75rem' }}
                            MenuProps={{
                              PaperProps: {
                                sx: {
                                  maxHeight: 400,
                                  minWidth: 200,
                                  '& .MuiMenuItem-root': {
                                    whiteSpace: 'normal',
                                    wordBreak: 'break-word',
                                    padding: '8px 12px',
                                    minHeight: 'auto',
                                    lineHeight: 1.4,
                                    overflow: 'visible',
                                    color: '#000',
                                    fontSize: '0.75rem',
                                    '&:hover': {
                                      backgroundColor: '#f5f5f5'
                                    }
                                  }
                                }
                              }
                            }}
                          >
                            {TSR_TIME_UNITS.map((unit) => (
                              <MenuItem key={unit} value={unit} sx={{ fontSize: '0.75rem' }}>{unit}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </TableCell>
                  <TableCell sx={{ width: '160px' }}>
                    {renderValueAtInstallationCell(record, index, 'airframeValueAtInstallation', 'airframeVal', disableInstallSnapshots)}
                  </TableCell>
                  <TableCell sx={{ width: '160px' }}>
                    {renderValueAtInstallationCell(record, index, 'parentAssemblyValueAtInstallation', 'parentVal', isAirframeAssembly || disableInstallSnapshots)}
                  </TableCell>
                  <TableCell sx={{ width: '180px' }}>
                    <Grid container spacing={1}>
                      <Grid item xs={7}>
                        <TextField
                          size="small"
                          fullWidth
                          type="text"
                          value={record.remainingTime?.value || ''}
                          onChange={(e) => handleUpdateTimeSinceRecord(index, 'remainingTime.value', e.target.value)}
                          placeholder={record.remainingTime?.unit === 'Hours' ? 'HH:MM' : record.remainingTime?.unit === 'Years' ? 'YY.MM (e.g., 2.10)' : '0'}
                          disabled
                          sx={{ 
                            '& .MuiInputBase-input': { 
                              backgroundColor: '#f5f5f5',
                              cursor: 'not-allowed',
                              fontSize: '0.75rem'
                            } 
                          }}
                        />
                      </Grid>
                      <Grid item xs={5}>
                        <FormControl size="small" fullWidth>
                          <Select
                            value={record.elapsedTime?.unit || 'Hours'}
                            disabled
                            title="Remaining uses the same unit as Elapsed (including when Limit uses a different calendar format)."
                            sx={{ 
                              fontSize: '0.75rem',
                              '& .MuiSelect-select': { 
                                backgroundColor: '#f5f5f5',
                                cursor: 'not-allowed'
                              } 
                            }}
                            MenuProps={{
                              PaperProps: {
                                sx: {
                                  maxHeight: 400,
                                  minWidth: 200,
                                  '& .MuiMenuItem-root': {
                                    whiteSpace: 'normal',
                                    wordBreak: 'break-word',
                                    padding: '8px 12px',
                                    minHeight: 'auto',
                                    lineHeight: 1.4,
                                    overflow: 'visible',
                                    color: '#000',
                                    fontSize: '0.75rem',
                                    '&:hover': {
                                      backgroundColor: '#f5f5f5'
                                    }
                                  }
                                }
                              }
                            }}
                          >
                            {TSR_TIME_UNITS.map((unit) => (
                              <MenuItem key={unit} value={unit} sx={{ fontSize: '0.75rem' }}>{unit}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </TableCell>
                  <TableCell sx={{ width: '100px' }}>
                    <Box>
                        <TextField
                        size="small"
                        type="date"
                        fullWidth
                        value={record.startDate ? new Date(record.startDate).toISOString().split('T')[0] : ''}
                        onChange={(e) => !readOnly && handleUpdateTimeSinceRecord(index, 'startDate', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{ '& .MuiInputBase-input': { fontSize: '0.75rem' } }}
                      />
                      <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary', display: 'block', mt: 0.5 }}>
                        Date when tracking began (e.g., installation, overhaul)
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ width: '100px' }}>
                    <Box>
                        <TextField
                        size="small"
                        type="date"
                        fullWidth
                        value={record.asOnDate ? new Date(record.asOnDate).toISOString().split('T')[0] : ''}
                        onChange={(e) => !readOnly && handleUpdateTimeSinceRecord(index, 'asOnDate', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{ '& .MuiInputBase-input': { fontSize: '0.75rem' } }}
                      />
                      <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary', display: 'block', mt: 0.5 }}>
                        Effective date for elapsed time calculation (auto-calculates for Years/Calendar Days)
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ width: '120px' }}>
                    <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>—</Typography>
                  </TableCell>
                  <TableCell sx={{ width: '120px' }}>
                    <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>—</Typography>
                  </TableCell>
                  <TableCell sx={{ width: '120px' }}>
                    <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>—</Typography>
                  </TableCell>
                  <TableCell sx={{ width: '80px' }}>
                    {!readOnly && (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveTimeSinceRecord(index)}
                      >
                        <Delete />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              )})}
              {timeSinceRecords.length === 0 && (
                <TableRow>
                  <TableCell colSpan={14} align="center">
                    No time since records. Click "Add Record" to add one.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
    </>
  );
};

export default TimeSinceRecordsTable;
